/* eslint-disable no-nested-ternary */
import React, {FunctionComponent, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import _ from 'lodash';
import {ACCEPTED} from 'http-status-codes';
import Grid from '@material-ui/core/Grid';
import {Title} from '@treez-ui/core/layout';
import {TitleSize} from '@treez-ui/core/layout/Title';
import {ModalType, SnackbarType} from '@treez-ui/core/feedback/modals';
import {OwnerType} from '@treez/commons/sharedTypings/product';
import {
    PriceTierSearchParams,
    PriceTierWithHasProductsFlag,
} from '@treez/commons/sharedTypings/priceTier';
import {LinearProgress} from '@material-ui/core';
import UIWrapper from '../UIWrapper';
import {selectOrganizationState} from '~/redux/selectors/organizationSelectors';
import {PriceTierOptions} from './PriceTierOptions';
import {PriceTierListTable} from './PriceTierListTable';
import {generateRelativeEditPriceTierRoute} from '~/Routes';
import {
    openFeedbackModal,
    closeFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';
import {
    getPriceTierPresenceInCatalog,
    PriceTierCatalogPresence,
    searchPriceTiers,
    updatePriceTier,
} from '~/utils/ProductAPI';
import {replacePriceTier} from '~/redux/actions/OrganizationActions';
import {clearSnackbar, showSnackbar} from '~/redux/actions/SnackbarActions';
import {StoreAttributes} from '~/lib/directory';
import {PriceManagementSearchParams, PriceTierWithCatalogPresence} from '~/lib/sharedInterfaces';
import PriceTierReassignModal from './PriceTierReassignModal';
import {generateQueryString, parseQueryString} from '~/lib/routing';

interface RouteParams {
    id: string;
    organizationId: string;
}

interface PriceTierReassignModalData {
    open: boolean;
    priceTier?: PriceTierWithCatalogPresence;
}

export const PriceTierManagement: FunctionComponent<RouteComponentProps<RouteParams>> = (
    props: RouteComponentProps<RouteParams>
) => {
    const {
        match: {
            params: {organizationId: ownerId},
        },
        location,
        history,
    } = props;

    const {
        isFetching: isFetchingOrgGlobalState,
        organizationCatalogId,
        priceTiers: priceTiersGlobal,
        stores,
    } = useSelector(selectOrganizationState);

    const [priceTiers, setPriceTiers] = useState<PriceTierWithHasProductsFlag[]>([]);
    const [priceTierPresenceInCatalog, setPriceTierPresenceInCatalog] =
        useState<PriceTierCatalogPresence>({});
    const [priceTierPresenceNeedsUpdate, setPriceTierPresenceNeedsUpdate] =
        useState<boolean>(false);
    const [isSearchingPriceTiers, setIsSearchingPriceTiers] = useState<boolean>(false);

    const [priceTierReassignModalProps, setPriceTierReassignModalProps] =
        useState<PriceTierReassignModalData>({
            open: false,
            priceTier: undefined,
        });

    const storesByCatalogId: Record<number, StoreAttributes> = _.keyBy(stores, 'catalogId');

    // remove tokens info from url, if exists
    const params = new URLSearchParams(location.search);
    if (params.get('tokens') !== null) {
        params.delete('tokens');
        history.replace({
            search: params.toString(),
        });
    }

    const queryParams = useMemo(
        () => parseQueryString(location.search) as PriceManagementSearchParams,
        [location.search]
    );

    const isFetchingPriceTiers = isSearchingPriceTiers || isFetchingOrgGlobalState;

    const dispatch = useDispatch();

    useEffect(() => {
        if (!_.isEmpty(queryParams)) {
            setIsSearchingPriceTiers(true);
        } else if (priceTiersGlobal) {
            setPriceTiers(priceTiersGlobal);
            setPriceTierPresenceNeedsUpdate(true);
        }
    }, [priceTiersGlobal, queryParams]);

    useEffect(() => {
        if (isSearchingPriceTiers) {
            setIsSearchingPriceTiers(false);
            fetchPriceTiersBySearch()
                .then((tiers) => {
                    setPriceTiers(tiers);
                    setPriceTierPresenceNeedsUpdate(true);
                })
                .catch(() => {
                    setPriceTiers([]);
                });
        }
    }, [isSearchingPriceTiers]);

    useEffect(() => {
        if (priceTierPresenceNeedsUpdate && priceTiersGlobal) {
            const priceTierIds: number[] = priceTiersGlobal.map((tier) => tier.id);

            getPriceTierPresenceInCatalog(priceTierIds).then((data) => {
                const priceTierPresenceInCatalog: PriceTierCatalogPresence = data;

                setPriceTierPresenceInCatalog(priceTierPresenceInCatalog);
            });
            setPriceTierPresenceNeedsUpdate(false);
        }
    }, [priceTierPresenceNeedsUpdate]);

    const fetchPriceTiersBySearch = () => {
        const filters = _.chain(queryParams)
            .omitBy(_.isUndefined)
            .omit('label')
            .map((searchParamValue: string | string[], key) => ({
                key,
                values: _.isArray(searchParamValue)
                    ? searchParamValue
                    : _.reject(searchParamValue.split(','), _.isEmpty),
            }))
            .value();

        const searchParams = {
            searchString: queryParams.label ?? '',
            catalogId: organizationCatalogId,
            filters,
        } as PriceTierSearchParams;

        return searchPriceTiers(searchParams);
    };

    const getPriceTierWithCatalogPresence = (
        priceTiers: PriceTierWithHasProductsFlag[]
    ): PriceTierWithCatalogPresence[] => {
        if (_.isEmpty(priceTiers)) {
            return [];
        }

        return priceTiers.map((priceTier) => {
            const catalogPresence: string[] = [];

            if (!_.isEmpty(priceTierPresenceInCatalog[priceTier.id])) {
                priceTierPresenceInCatalog[priceTier.id].forEach((catalogId) => {
                    if (storesByCatalogId[catalogId]) {
                        const store = storesByCatalogId[catalogId];

                        catalogPresence.push(store.name);
                    }
                });
            }

            return {
                ...priceTier,
                catalogPresence,
            };
        });
    };

    const onFilter = (field: string, value: any) => {
        history.replace({
            search: generateQueryString({
                ...queryParams,
                [field]: value,
            }),
        });
    };

    const handleUpdatePriceTier = async (updatedPriceTier: PriceTierWithCatalogPresence) => {
        let response;
        try {
            response = await updatePriceTier(updatedPriceTier, organizationCatalogId);
        } catch (error) {
            console.error(error);
            openFeedbackModal(closeFeedbackModal, 'Error updating Price Tier', {
                type: ModalType.WARNING,
                bodyText: 'There was an error updating the price tier',
                cancelOption: true,
                onCancel: () => closeFeedbackModal(),
            });
        }

        if (response && response.status === ACCEPTED) {
            const {
                data: {data: updatedPriceTier},
            } = response;
            dispatch(replacePriceTier(updatedPriceTier));
            dispatch(
                showSnackbar({
                    onClose: () => {
                        dispatch(clearSnackbar());
                    },
                    type: SnackbarType.SUCCESS,
                    content: `Saved!`,
                })
            );
            closeFeedbackModal();
        }
    };

    const handleConvert = (priceTier: PriceTierWithCatalogPresence) => {
        const updatedPriceTier: PriceTierWithCatalogPresence = {
            ...priceTier,
            ownerId,
            ownerType: OwnerType.ORG,
        };

        handleUpdatePriceTier(updatedPriceTier);
    };

    const onClearFilter = (field: string) => {
        history.replace({
            search: generateQueryString(_.omit(queryParams, field)),
        });
    };

    const onEditTier = (tier: PriceTierWithCatalogPresence) => {
        const {pathname} = generateRelativeEditPriceTierRoute(tier.id);
        history.push({
            pathname,
            state: tier,
        });
    };

    const onAddTier = () => {
        const route = generateRelativeEditPriceTierRoute();
        history.push(route);
    };

    const onConvert = (tier: PriceTierWithCatalogPresence) => {
        openFeedbackModal(
            () => handleConvert(tier),
            'Convert Store Price Tier into Organization Price Tier?',
            {
                type: ModalType.WARNING,
                bodyText:
                    'Are you sure you want to convert store price tier into an organization price tier?',
                cancelOption: true,
                onCancel: () => closeFeedbackModal(),
            }
        );
    };

    const onReassignSubmit = () => {
        setIsSearchingPriceTiers(true);
        setPriceTierPresenceNeedsUpdate(true);
    };

    const onReassignModalOpen = (priceTier: PriceTierWithCatalogPresence) => {
        setPriceTierReassignModalProps({
            open: true,
            priceTier,
        });
        closeFeedbackModal();
    };

    const onReassignModalClose = () => {
        setPriceTierReassignModalProps({
            open: false,
            priceTier: undefined,
        });
    };

    const onActiveChange = (priceTier: PriceTierWithCatalogPresence, isActive: boolean) => {
        if (priceTier.hasProducts && isActive === false) {
            openFeedbackModal(
                () => onReassignModalOpen(priceTier),
                'Price Tier Cannot Be Deactivated',
                {
                    type: ModalType.WARNING,
                    bodyText:
                        'This price tier cannot be deactivated because it has products currently assigned to it. Do you want to reassign those products to a different price tier?',
                    cancelOption: true,
                    onCancel: () => closeFeedbackModal(),
                    buttonText: 'Yes',
                }
            );
        } else {
            const updatedPriceTier: PriceTierWithCatalogPresence = {
                ...priceTier,
                isActive,
            };

            handleUpdatePriceTier(updatedPriceTier);
        }
    };

    return (
        <UIWrapper>
            <Grid container>
                <Grid className="headline">
                    <Title size={TitleSize.MEDIUM} text="Pricing Tier Management" />
                </Grid>
                <Grid item sm={12}>
                    <PriceTierOptions
                        searchParams={queryParams}
                        onAdd={onAddTier}
                        onClearFilter={onClearFilter}
                        onFilter={onFilter}
                    />
                </Grid>
                <Grid item sm={12}>
                    {isFetchingPriceTiers ? (
                        <div>
                            <LinearProgress />
                            <span>Loading...</span>
                        </div>
                    ) : _.isEmpty(priceTiers) ? (
                        <div className="no-price-tiers-found">
                            <span>No Price Tiers Found</span>
                        </div>
                    ) : (
                        <PriceTierListTable
                            onActiveChange={onActiveChange}
                            onConvert={onConvert}
                            onEditTier={onEditTier}
                            onReassign={onReassignModalOpen}
                            priceTiers={getPriceTierWithCatalogPresence(priceTiers)}
                            readOnly={false}
                        />
                    )}
                </Grid>
            </Grid>
            {priceTierReassignModalProps.open && (
                <PriceTierReassignModal
                    onClose={onReassignModalClose}
                    open={priceTierReassignModalProps.open}
                    priceTier={priceTierReassignModalProps.priceTier}
                    onSubmit={onReassignSubmit}
                />
            )}
        </UIWrapper>
    );
};
